import { useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ExtraQuestionsEditor } from "../components/QuestionDesign";
import { eventHiddenFromFamilies, staff } from "../domain/data";
import { useEvents } from "../state/events";
import {
  questionDesignError,
  type FormQuestion,
} from "../domain/questions";
import { ErrorFlash } from "../components/ErrorFlash";
import { PrototypeBanner, StaffNav } from "../components/ui";

export function StaffEventForm() {
  const { eventId = "" } = useParams();
  const navigate = useNavigate();
  const { getEvent, updateEvent } = useEvents();
  const event = getEvent(eventId);
  const [questions, setQuestions] = useState<FormQuestion[]>(
    event?.questions ?? [],
  );
  const [error, setError] = useState("");

  if (!event) {
    return (
      <div className="app">
        <PrototypeBanner />
        <main className="shell">
          <p>Event not found.</p>
          <Link to="/staff/events">Back to events</Link>
        </main>
      </div>
    );
  }

  const hidden = eventHiddenFromFamilies(event);
  const locked = event.kind !== "rsvp" || hidden;

  const liftEvent = event;

  function onSubmit(formEvent: FormEvent) {
    formEvent.preventDefault();
    if (locked) return;
    const extraError = questionDesignError(questions);
    if (extraError) {
      setError(extraError);
      return;
    }
    updateEvent(liftEvent.id, { questions });
    navigate(`/staff/events/${liftEvent.id}`);
  }

  return (
    <div className="app">
      <PrototypeBanner />
      <main className="shell">
        <StaffNav />
        <p className="meta">
          Staff · {staff.given} {staff.family} · event form
        </p>
        <h1>{locked ? "View event questions" : "Design event questions"}</h1>
        <p className="lede">
          Extra questions for {event.name}. Families answer these when they
          RSVP. Short text, paragraph, yes/no, radios, checkboxes, and
          dropdowns.
        </p>

        {event.kind !== "rsvp" ? (
          <div className="flash">
            Afterschool programs are staff attendance only. Add questions on an
            RSVP trip.
          </div>
        ) : hidden ? (
          <div className="flash">
            Past or archived trips stay as families saw them.
          </div>
        ) : null}

        <form className="design" onSubmit={onSubmit}>
          <section className="design-section">
            <div className="wire design-section-head">
              <div>
                <div className="meta">{event.dateLabel}</div>
                <strong>RSVP questions</strong>
              </div>
              <div className="tag muted">{questions.length} questions</div>
            </div>
            <ExtraQuestionsEditor
              questions={questions}
              locked={locked}
              emptyLabel="No extra questions on this trip"
              onChange={setQuestions}
            />
          </section>

          {error ? <ErrorFlash key={error} message={error} /> : null}

          <div className="actions">
            <button type="submit" disabled={locked}>
              Save questions
            </button>
            <Link className="btn ghost" to={`/staff/events/${event.id}`}>
              Cancel
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
